// Read-only "Invoice Summary" grid for the shared detail modal (app.md §8).
// Mirrors the Import Control editor's field set, labels, and layout
// (EditableInvoiceForm) so every step's modal reads the same — here the values
// are plain read-only text. The AI's notes are not in this grid: they sit under
// History's upload row, at every step (owner, 2026-09-25 — D-147; D-330).
import { Field, Input, mergeClasses } from '@fluentui/react-components'
import type { Flag, Invoice } from '../lib/types'
import { formatCurrency, formatNumericDate } from '../lib/format'
import { useMoneyInputStyles, useRequiredStyles, useSummaryGridStyles } from '../lib/theme'
import { useDialogFormStyles } from './dialogForm'
import { fieldBorder, summaryFieldFlags, summaryFieldTooltip, type SummaryField } from '../lib/flagged'
import { FlagTooltip } from './FlagTooltip'


// `align="right"` is opt-in (owner item 2, 2026-08-19): this helper renders
// TEN fields (owner item 7, 2026-08-19, dropped the eleventh — "Path to
// Point Click Care"), always including the Internal ID (a raw uuid) — blanket-styling
// the helper would right-align that too. Only the four money lines pass it.
//
// NEVER RED (D-248): red means a required field is empty and fillable here,
// and nothing on this panel can be filled in. A field tied to an unresolved
// flag is YELLOW, from the same `lib/flagged.ts` the editor asks.
function ReadOnlyField({
  label,
  value,
  className,
  align,
  flagTypes,
  tip,
}: {
  label: string
  value: string
  className?: string
  align?: 'right'
  /** The unresolved flags tied to this field (`summaryFieldFlags`). */
  flagTypes: readonly string[]
  /** Its hover text (`summaryFieldTooltip`), which names only flags the
      invoice has (D1-01). */
  tip: string | undefined
}) {
  const ms = useMoneyInputStyles()
  const rs = useRequiredStyles()
  return (
    <FlagTooltip text={tip}>
      <Field label={label} className={className}>
        <Input
          className={fieldBorder(false, flagTypes) === 'flagged' ? rs.flagged : undefined}
          value={value}
          readOnly
          appearance="filled-darker"
          input={align === 'right' ? { className: ms.moneyText } : undefined}
        />
      </Field>
    </FlagTooltip>
  )
}

/** NO `showInternalId` PROP ANY MORE (owner item 13, 2026-08-28). It existed
    for one caller — `EditableInvoiceForm`'s "Referenced invoice" pop-up — which
    hid the Internal ID because that pop-up existed so a reviewer never had to
    read a bare uuid off a duplicate flag's detail. The owner asked that pop-up
    to show all TEN invoice-level fields the detail modal shows, and the
    original reasoning survives it: what was refused was a uuid as the only
    identification of an invoice, in flag prose. In a labelled "Internal ID"
    field beside the vendor and the invoice number it is one field of ten. With
    that caller gone the prop had none, so this panel renders the same ten
    fields for everyone. */
/** `amountsDoNotReconcile`: the amounts do not reconcile (within the
    three-cent tolerance, D-151), so Tax, Shipping, Subtotal and Total all take
    a yellow border together — the same four the editable form marks (owner,
    2026-09-25 — D-148; yellow since D-248). The caller works it out from the
    stored line items. `flags`: the invoice's flag records, so a field tied to
    one still open is yellow here too. */
export function InvoiceHeaderPanel({
  invoice,
  flags = [],
  amountsDoNotReconcile = false,
}: {
  invoice: Invoice
  flags?: readonly Flag[]
  amountsDoNotReconcile?: boolean
}) {
  const s = useSummaryGridStyles()
  const f = useDialogFormStyles()
  const text = (v: string | undefined | null) => (v == null || v === '' ? '—' : v)
  const flagged = (field: SummaryField) => summaryFieldFlags(flags, field, { amountsDoNotReconcile })
  const tip = (field: SummaryField) => summaryFieldTooltip(flags, field, { amountsDoNotReconcile })
  return (
    <div className={mergeClasses(f.grid2, s.grid)}>
      <ReadOnlyField label="Company" value={text(invoice.company_name)} flagTypes={flagged('company')} tip={tip('company')} />
      <ReadOnlyField label="Vendor" value={text(invoice.vendor_name)} flagTypes={flagged('vendor')} tip={tip('vendor')} />
      <ReadOnlyField label="Internal ID" value={text(invoice.id)} flagTypes={flagged('internalId')} tip={tip('internalId')} />
      <ReadOnlyField label="External ID" value={text(invoice.external_invoice_id)} flagTypes={flagged('externalId')} tip={tip('externalId')} />
      <ReadOnlyField label="Invoice Date" value={formatNumericDate(invoice.invoice_date)} flagTypes={flagged('invoiceDate')} tip={tip('invoiceDate')} />
      <ReadOnlyField label="Received Date" value={formatNumericDate(invoice.received_at)} flagTypes={flagged('receivedDate')} tip={tip('receivedDate')} />
      {/* THE TWO CHARGES, THEN THE TWO SUMMARY FIGURES — the same order, and for
          the same reason, as `EditableInvoiceForm`'s own header (owner,
          2026-08-14): "put Shipping and Tax together above Subtotal and Total".
          This panel reads the same as that editor at every later step. A null
          charge reads $0.00, as every money value does (`formatCurrency`). */}
      <ReadOnlyField label="Tax" value={formatCurrency(invoice.tax)} align="right" flagTypes={flagged('tax')} tip={tip('tax')} />
      <ReadOnlyField label="Shipping" value={formatCurrency(invoice.shipping)} align="right" flagTypes={flagged('shipping')} tip={tip('shipping')} />
      <ReadOnlyField label="Subtotal" value={formatCurrency(invoice.subtotal)} align="right" flagTypes={flagged('subtotal')} tip={tip('subtotal')} />
      <ReadOnlyField label="Total" value={formatCurrency(invoice.total)} align="right" flagTypes={flagged('total')} tip={tip('total')} />
      {/* NO PRIOR BALANCE FIELD (owner item 3, 2026-08-17), dropped in step with
          `EditableInvoiceForm`'s copy of it — this panel exists to read the same
          as that editor, so the two move together. The column and the
          `PriorBalanceIncluded` flag both remain; the figure reaches the reviewer
          in the extraction note instead (`pass1-extraction.md` `7.1.0`).
          NO CURRENCY FIELD either (owner, 2026-08-14 — `docs/decisions.md` D-8): "it
          should never be used". The column and the `NonUSDCurrency` flag remain;
          the flag reads on the Flags panel like every other explicit flag.
          NO "PATH TO POINT CLICK CARE" ROW either (owner item 7, 2026-08-19,
          retiring owner item 13 of 2026-08-12 above): the term is retired
          (`docs/glossary.md`) along with the row and the `showPathToPointClickCare`
          prop that gated it — see `docs/decisions.md` for the numbered entry.
          This grid is a flat TEN fields, always: Company, Vendor, Internal
          ID, External ID, Invoice Date, Received Date, Tax, Shipping,
          Subtotal, Total — five even rows, no orphan gap and no conditional
          row at all since owner item 13, 2026-08-28. */}
    </div>
  )
}
